import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SquareButton from '@/components/SquareButton';
import { postJson } from '@/lib/api';

const inputCls = 'mt-2 h-12 border-2 border-[hsl(var(--c-ink))] bg-white px-3 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[hsl(var(--c-mango-orange))]';
const labelCls = 'eyebrow text-[hsl(var(--c-ink))]/70';

const BUSINESS_TYPES = ['Produce Distributor', 'Grocery Store', 'Restaurant', 'Fruit Market', 'Juice Bar', 'Hotel / Resort', 'Event Vendor', 'Food Service Company', 'Other'];
const QUANTITIES = ['50+', '100+', '250+', '500+', '1,000+', '5,000+', 'Custom Quantity'];

export default function WholesaleInquiryForm() {
  const [form, setForm] = useState({
    business_name: '', contact_name: '', email: '', phone: '',
    business_type: '', quantity_needed: '', city: '', state: '',
    preferred_delivery_date: '', notes: '', honeypot: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.contact_name || !form.email || !form.business_type || !form.quantity_needed) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await postJson('/wholesale-inquiries', { ...form, source_page: 'wholesale' });
      setSubmitted(true);
      toast.success(res.message || 'Wholesale inquiry received!');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="py-12 text-center" data-testid="wholesale-success">
        <div className="mx-auto inline-flex h-16 w-16 items-center justify-center bg-[hsl(var(--c-leaf))] border-2 border-[hsl(var(--c-ink))]">
          <CheckCircle2 className="h-8 w-8 text-white" />
        </div>
        <h3 className="mt-6 font-heading text-3xl sm:text-4xl font-extrabold">Wholesale inquiry received</h3>
        <p className="mt-3 text-[hsl(var(--c-ink-soft))] max-w-lg mx-auto">
          Our team will review current harvest availability and reply with pricing, logistics, and a custom quote tailored to your needs.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="grid gap-5" data-testid="wholesale-inquiry-form">
      <input type="text" name="website" value={form.honeypot} onChange={(e) => update('honeypot', e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" />

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="w-biz" className={labelCls}>Business Name *</Label>
          <Input id="w-biz" data-testid="wholesale-business-name-input" required value={form.business_name} onChange={(e) => update('business_name', e.target.value)} className={inputCls} />
        </div>
        <div>
          <Label htmlFor="w-contact" className={labelCls}>Contact Name *</Label>
          <Input id="w-contact" data-testid="wholesale-contact-name-input" required value={form.contact_name} onChange={(e) => update('contact_name', e.target.value)} className={inputCls} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="w-email" className={labelCls}>Email *</Label>
          <Input id="w-email" type="email" data-testid="wholesale-email-input" required value={form.email} onChange={(e) => update('email', e.target.value)} className={inputCls} />
        </div>
        <div>
          <Label htmlFor="w-phone" className={labelCls}>Phone</Label>
          <Input id="w-phone" data-testid="wholesale-phone-input" value={form.phone} onChange={(e) => update('phone', e.target.value)} className={inputCls} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className={labelCls}>Business Type *</Label>
          <Select value={form.business_type} onValueChange={(v) => update('business_type', v)}>
            <SelectTrigger data-testid="wholesale-business-type-trigger" className={inputCls}><SelectValue placeholder="Select a category" /></SelectTrigger>
            <SelectContent>
              {BUSINESS_TYPES.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className={labelCls}>Quantity Needed *</Label>
          <Select value={form.quantity_needed} onValueChange={(v) => update('quantity_needed', v)}>
            <SelectTrigger data-testid="wholesale-quantity-trigger" className={inputCls}><SelectValue placeholder="Choose an estimate" /></SelectTrigger>
            <SelectContent>
              {QUANTITIES.map((q) => (<SelectItem key={q} value={q}>{q}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="w-city" className={labelCls}>City</Label>
          <Input id="w-city" data-testid="wholesale-city-input" value={form.city} onChange={(e) => update('city', e.target.value)} className={inputCls} />
        </div>
        <div>
          <Label htmlFor="w-state" className={labelCls}>State</Label>
          <Input id="w-state" data-testid="wholesale-state-input" value={form.state} onChange={(e) => update('state', e.target.value)} className={inputCls} />
        </div>
        <div>
          <Label htmlFor="w-date" className={labelCls}>Preferred Delivery</Label>
          <Input id="w-date" type="date" data-testid="wholesale-date-input" value={form.preferred_delivery_date} onChange={(e) => update('preferred_delivery_date', e.target.value)} className={inputCls} />
        </div>
      </div>

      <div>
        <Label htmlFor="w-notes" className={labelCls}>Additional Notes</Label>
        <Textarea id="w-notes" data-testid="wholesale-notes-input" value={form.notes} onChange={(e) => update('notes', e.target.value)} className={`${inputCls} h-auto py-3`} rows={4} placeholder="Variety preferences, delivery cadence, ripening preferences, etc." />
      </div>

      <SquareButton type="submit" disabled={submitting} variant="ink" size="lg" fullWidth testId="wholesale-inquiry-form-submit-button">
        {submitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>) : 'Request Wholesale Pricing'}
      </SquareButton>
    </form>
  );
}
